import { Resizable } from 're-resizable';
import React, { SetStateAction, useCallback, useMemo, useState } from 'react';

import { Panel as PanelType, PythonPanelConfig } from '@mpm-druid/types';
import EditableField from '@shared/components/EditableField';
import { Panel } from '../Panel';
import { PythonCode } from './PythonCode';

import styles from './PanelModal.module.scss';

type PanelData = PanelType['data'];

export function PanelModalContent({
  data,
  id,
  isEditing,
  name,
  onDataChange,
  onNameChange
}: {
  data: PanelData;
  id: string;
  isEditing: boolean;
  name: string;
  onDataChange: React.Dispatch<SetStateAction<PanelData>>;
  onNameChange: React.Dispatch<SetStateAction<string>>;
}) {
  const [localData, setLocalData] = useState<PanelData>(data);

  const handlePythonConfigChange = useCallback(
    (
      updateConfig: (prev: PythonPanelConfig) => PythonPanelConfig,
      shouldApply = true
    ) => {
      const applyChanges = (prev: PanelData) => {
        if (prev.type !== 'python') {
          return prev;
        }

        return { ...prev, config: updateConfig(prev.config) };
      };
      onDataChange(applyChanges);

      if (shouldApply) {
        setLocalData(applyChanges);
      }
    },
    [onDataChange]
  );

  const panel = useMemo<PanelType>(() => {
    return {
      // It avoids to modify the original Streamlit instance (affecting the dashboard)
      id: isEditing ? `edit_${id}` : id,
      data: localData,
      name
    };
  }, [id, isEditing, localData, name]);

  return (
    <div className={styles.container}>
      <Resizable
        minWidth="20%"
        maxWidth="80%"
        defaultSize={{ width: '50%', height: '100%' }}
        enable={{ right: true }}
        handleComponent={{
          right: (
            <div className={styles.handle}>
              <div className={styles.handleInner} />
            </div>
          )
        }}
        handleStyles={{ right: { right: 0, width: 0 } }}
      >
        <div className={styles.configurationContainer}>
          {data.type === 'python' && (
            <PythonCode
              config={data.config}
              name={name}
              onChange={handlePythonConfigChange}
              onNameChange={onNameChange}
            />
          )}
        </div>
      </Resizable>

      <div className={styles.panelContainer}>
        <div className={styles.panelName}>
          <div className={styles.nameContainer}>
            <EditableField
              canEdit
              currentName={name}
              editHandler={onNameChange}
              placeholder="Panel title"
            />
          </div>
        </div>
        <div className={styles.panelPreview}>
          <div className={styles.panel}>
            <Panel panel={panel} />
          </div>
        </div>
      </div>
    </div>
  );
}
